import SectionHeading from '@/components/base/SectionHeading';
import EquityAndDrawdown from '@/components/feature/EquityAndDrawdown';
import MonthlyReturnsBars from '@/components/feature/MonthlyReturnsBars';
import MonthlyWinRateBars from '@/components/feature/MonthlyWinRateBars';
import Reveal from '@/components/feature/Reveal';
import ZoomableImage from '@/components/feature/ZoomableImage';
import PerformanceBoundary from '@/components/feature/PerformanceBoundary';

/**
 * Real MetaTrader 5 terminal balance/equity screenshot for this account.
 * Wide, short export — its container uses a matching aspect ratio below.
 */
const EQUITY_SHOT = '/image/mt5-equity.png';

export default function PerformanceCharts() {
  return (
    <section id="equity" className="py-14 md:py-20 bg-background-100 border-y border-background-200 scroll-mt-32 md:scroll-mt-36">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              align="left"
              eyebrow="Equity & returns"
              title="Growth curve and month-by-month results"
              subtitle="The balance curve below is built from every closed trade. Flip between weekly and monthly views, and the underwater chart underneath shows how deep each pullback ran from the peak."
            />
          </Reveal>

          <PerformanceBoundary label="equity curves">
            {(data) => {
              const { monthlyReturns, monthlyBreakdown, accountSnapshot, weeklyEquity, monthlyEquity } = data;
              const avgWinRate =
                monthlyBreakdown.length > 0
                  ? monthlyBreakdown.reduce((sum, m) => sum + m.winRate, 0) / monthlyBreakdown.length
                  : 0;
              const monthsAboveHalf = monthlyBreakdown.filter((m) => m.winRate >= 50).length;

              return (
                <>
                  <div className="mt-10">
                    <Reveal>
                      <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                        <div>
                          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                            Account Equity Growth
                          </h3>
                          <p className="mt-1 text-xs md:text-sm text-foreground-500">
                            Hover or tap the curve to see the account balance at each point (USC)
                          </p>
                        </div>
                        <div className="mt-6">
                          <EquityAndDrawdown weekly={weeklyEquity} monthly={monthlyEquity} />
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-4 border-t border-background-200 pt-5">
                          <div>
                            <p className="text-xs text-foreground-500">Peak Balance</p>
                            <p className="mt-1.5 font-num text-lg md:text-xl font-bold text-foreground-900">
                              {accountSnapshot.peakBalance.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                              <span className="ml-1 text-xs font-normal text-foreground-400">USC</span>
                            </p>
                            <p className="mt-0.5 text-xs text-foreground-500">all-time high</p>
                          </div>
                          <div>
                            <p className="text-xs text-foreground-500">Current Drawdown</p>
                            <p
                              className={`mt-1.5 font-num text-lg md:text-xl font-bold ${
                                accountSnapshot.currentDrawdown < 0
                                  ? 'text-secondary-500'
                                  : 'text-primary-600'
                              }`}
                            >
                              {accountSnapshot.currentDrawdown >= 0
                                ? 'At peak'
                                : `${accountSnapshot.currentDrawdown.toFixed(2)}%`}
                            </p>
                            <p className="mt-0.5 text-xs text-foreground-500">from peak right now</p>
                          </div>
                        </div>

                        {/* Terminal cross-check — the platform's own plot of the same account */}
                        <div className="mt-6 border-t border-background-200 pt-5">
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-100 text-accent-800">
                              <i className="ri-line-chart-line text-sm" aria-hidden="true"></i>
                            </span>
                            <h4 className="font-heading text-sm font-semibold text-foreground-950">
                              Straight from the terminal
                            </h4>
                            <span className="text-xs text-foreground-500">
                              — the same account, as MetaTrader 5 plots it
                            </span>
                          </div>

                          <div className="relative mt-4 aspect-[40/9] w-full overflow-hidden rounded-md border border-background-200 bg-background-100">
                            <ZoomableImage
                              src={EQUITY_SHOT}
                              alt={`MetaTrader 5 terminal balance and equity curve for account ${accountSnapshot.accountNumber}`}
                              title="MT5 · Balance / equity curve"
                              className="absolute inset-0 h-full w-full"
                              imageClassName="h-full w-full object-contain object-top"
                              empty={
                                <div className="absolute inset-3 flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-background-300 bg-background-50/70 px-6 text-center">
                                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                                    <i className="ri-image-add-line text-xl" aria-hidden="true"></i>
                                  </span>
                                  <p className="text-xs font-semibold text-foreground-800 md:text-sm">
                                    MT5 terminal equity curve screenshot
                                  </p>
                                  <p className="text-[11px] leading-relaxed text-foreground-500">
                                    Send the export and this slot shows the platform&apos;s own plot of the account.
                                  </p>
                                </div>
                              }
                            />
                          </div>

                          <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                            The curve above is rebuilt by this site from the raw trade log; the screenshot is the
                            MetaTrader 5 terminal&apos;s own chart of the same account — a second, independent view you
                            can enlarge and check point for point.
                          </p>
                        </div>
                      </div>
                    </Reveal>
                  </div>

                  <Reveal delay={120}>
                    <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                            Monthly Return Distribution
                          </h3>
                          <p className="mt-1 text-xs md:text-sm text-foreground-500">
                            Each month&apos;s gain or loss on top, with the compounded growth across the same months in the panel below — hover or tap either one for the exact figures
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                          <span className="inline-flex items-center gap-2 text-xs text-foreground-600">
                            <span className="w-2.5 h-2.5 rounded-sm bg-primary-500" aria-hidden="true"></span>
                            Profit
                          </span>
                          <span className="inline-flex items-center gap-2 text-xs text-foreground-600">
                            <span className="w-2.5 h-2.5 rounded-sm bg-secondary-400" aria-hidden="true"></span>
                            Loss
                          </span>
                          <span className="inline-flex items-center gap-2 text-xs text-foreground-600">
                            <span className="w-4 h-0.5 rounded-full bg-accent-500" aria-hidden="true"></span>
                            Cumulative return
                          </span>
                        </div>
                      </div>
                      <div className="mt-6">
                        <MonthlyReturnsBars data={monthlyReturns} />
                      </div>
                    </div>
                  </Reveal>

                  <Reveal delay={160}>
                    <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950">
                            Monthly Win Rate
                          </h3>
                          <p className="mt-1 text-xs md:text-sm text-foreground-500">
                            Share of each month&apos;s closed trades that were winners — keep the bars above the 50% line
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-background-200/70 px-2.5 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs font-semibold text-foreground-800">
                            Avg {avgWinRate.toFixed(1)}%
                          </span>
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-2.5 sm:px-3 py-1 sm:py-1.5 text-[11px] sm:text-xs font-semibold text-accent-900">
                            {monthsAboveHalf}/{monthlyBreakdown.length} &gt;50%
                          </span>
                        </div>
                      </div>
                      <div className="mt-6">
                        <MonthlyWinRateBars data={monthlyBreakdown} />
                      </div>
                    </div>
                  </Reveal>
                </>
              );
            }}
          </PerformanceBoundary>
        </div>
      </div>
    </section>
  );
}